/**
 * Price formatting and visibility utilities.
 *
 * Centralizes all price-related presentation logic.
 *
 * Related utilities:
 *   - resolvePrice()  — shared/utils/productUtils.js  (resolves effective price from history / brutto)
 *
 * Usage map:
 *   formatCurrency()              — ProductRow, ProductTile, SupplementaryProductsTable
 *   formatPriceLabelShort()       — CatalogMainBar / CatalogAdvancedFilters (range slider labels)
 *   TODO getOmnibusLowestPrice()       — ProductView → PriceCard
 */

import { AVAILABILITY_STATUS } from '../constants/availabilityStatus.js'
import { i18n } from '../i18n/i18n.js'

const { SOLD, RESERVED } = AVAILABILITY_STATUS

// ── Base formatters ─────────────────────────────────────────────────────────────

/**
 * Parses a raw price value into a valid positive number rounded to 2 decimal places,
 * or null if the value is missing/zero/non-numeric.
 *
 * Handles the following formats a Polish CRM may emit:
 *   - integer or float:                533250, 12.34, 0.5
 *   - numeric string (dot-decimal):    '533250', '12.34', '9.41e+000'
 *   - simple comma-decimal:            '32,95'  → 32.95
 *   - space-separated thousands:       '1 000 958,54' → 1000958.54
 *   - European full format:            '1.205,65' → 1205.65, '1.234.112,86' → 1234112.86
 *     (dot = thousands separator, comma = decimal — heuristic: both present)
 *
 * Ambiguous inputs:
 *   - '1.205' alone (no comma): treated as 1.205 (dot = decimal).
 *     Without locale info a dot-only string is indistinguishable from
 *     a US/EN decimal or a European-thousands integer.
 *
 * Returns null for: null, undefined, 0, negative values, NaN.
 *
 * @param {*} raw
 * @returns {number|null}
 */
function parsePrice(raw) {
  if (raw == null) return null
  let str = String(raw).trim()

  // 1. Strip whitespace used as thousands separator: '1 000 958,54' → '1000958,54'
  str = str.replace(/\s+/g, '')

  // 2. Normalize decimal separator.
  //    Heuristic: if string contains BOTH a dot AND a comma — European format
  //    where dot = thousands separator and comma = decimal separator.
  //    e.g. '1.205,65' → strip dots → '1205,65' → comma → dot → '1205.65'
  //    Without both characters, comma alone = decimal  (e.g. '32,95' → '32.95').
  //    Dot alone = decimal or scientific notation — leave for parseFloat.
  if (str.includes(',')) {
    if (str.includes('.')) {
      // European format: dot is thousands separator — strip all dots, then comma → dot
      str = str.replace(/\./g, '').replace(',', '.')
    } else {
      // Simple comma-decimal: '32,95' → '32.95'
      str = str.replace(',', '.')
    }
  }

  const n = parseFloat(str)
  if (isNaN(n) || n <= 0) return null
  return Math.round(n * 100) / 100
}

/**
 * Formats a number as a currency string using the given locale and currency code.
 *
 * Defaults to Polish locale + PLN, but accepts any IETF locale tag and
 * ISO 4217 currency code (EUR, USD, CHF, …).
 *
 * @param {number} value
 * @param {Object} [options]
 * @param {string} [options.currency='PLN']   ISO 4217 currency code
 * @param {string} [options.locale='pl-PL']   IETF locale tag
 * @param {number} [options.digits=2]         decimal places
 * @returns {string}  e.g. "357 779,00 zł" | "357,779.00 EUR"
 */
export function formatCurrency(value, { currency = 'PLN', locale = 'pl-PL', digits = 2 } = {}) {
  return value.toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

// ── Slider label format ────────────────────────────────────────────────────────

/**
 * Formats a price value as an abbreviated label for range slider display.
 *
 * Uses i18n for the unit suffix ("tyś." / "mln" in pl, "K" / "M" in en)
 * and the active locale for number formatting (decimal/thousands separators).
 *
 * @param {number} value  price in base currency units
 * @returns {string}  e.g. 420000 → "420 tyś." (pl) / "420K" (en), 1400000 → "1,40 mln" (pl) / "1.40M" (en)
 */
export function formatPriceLabelShort(value) {
  // Map i18n locale (pl_PL) → IETF BCP 47 tag (pl-PL) for Intl APIs
  const locale = (i18n.locale ?? 'pl_PL').replace('_', '-')

  if (value >= 1_000_000) {
    const num = (value / 1_000_000).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return `${num} ${i18n.t('common.price_millions')}`
  }
  const num = (value / 1_000).toLocaleString(locale, {
    maximumFractionDigits: 0,
  })
  return `${num} ${i18n.t('common.price_thousands')}`
}

// ── Visibility logic ───────────────────────────────────────────────────────────

/**
 * Determines whether a product's price should be hidden from the user.
 *
 * A price is hidden when any of the following is true:
 *   - pricesVisible is false                        — global "show prices" toggle is off
 *   - product.price_visibility === false             — product-level flag (boolean only)
 *   - product is sold AND pricesSoldVisible is false
 *   - product is reserved AND pricesReservedVisible is false
 *
 * CRM contract: `price_visibility` is always a boolean (`true` / `false`).
 * When the field is absent it defaults to `true` (visible).
 *
 * @param {Object}  product
 * @param {Object}  [options]
 * @param {boolean} [options.pricesVisible=true]           global toggle
 * @param {boolean} [options.pricesSoldVisible=true]       from panelSettings.prices_visibility
 * @param {boolean} [options.pricesReservedVisible=true]   from panelSettings.prices_visibility
 * @returns {boolean}
 */
export function isPriceHidden(product, {
  pricesVisible = true,
  pricesSoldVisible = true,
  pricesReservedVisible = true,
} = {}) {
  if (!pricesVisible) return true

  // Product-level flag — CRM guarantees boolean; absent = visible
  if ((product.price_visibility ?? true) === false) return true

  if (product.status === SOLD && !pricesSoldVisible) return true
  if (product.status === RESERVED && !pricesReservedVisible) return true

  return false
}


/**
 * Resolves the effective price or price per square meter of a product.
 *
 * Resolution order:
 *   1. Last entry in price_history (most recent price update from CRM/pipeline)
 *   2. price_brutto (static price baked into products.json)
 *   3. When sqm=true: price_m2_brutto or price_brutto / area, both from price_history then from product root
 *
 * Returns null if neither source yields a valid positive number.
 * A price of 0 is treated as missing (same as null).
 * Result is rounded to 2 decimal places.
 *
 * @param {Object} options
 * @param {Object} options.product
 * @param {Object} [options.entry]          optional price_history entry to resolve from (defaults to last entry in price_history)
 * @param {boolean} [options.sqm=false]   whether to return price per square meter
 * @returns {number|null}
 */
export function resolvePrice({product, entry, sqm = false}) {
  const historyEntry = entry ?? product.price_history?.at(-1)

  const totalResolvedPrice = 
    parsePrice(historyEntry?.price_brutto) ?? parsePrice(product.price_brutto)
  if (!totalResolvedPrice) return null

  if (sqm) {
    const area = product?.area_original
    if (!area) return null
    const sqmFromTotal = parsePrice(totalResolvedPrice / area);
    const sqmFromHistory = parsePrice(historyEntry?.price_m2_brutto ?? sqmFromTotal)
    const verifiedSqmPrice = sqmFromHistory != null 
      ? sqmFromHistory === sqmFromTotal
      : false
        
    if (verifiedSqmPrice) return sqmFromHistory
    return parsePrice(product.price_m2_brutto ?? totalResolvedPrice / area)
  }

  return totalResolvedPrice
}

// ── Strikethrough price resolution ───────────────────────────────────
/**
 * Returns the price before the current offer as a raw number.
 *
 * "Before the offer" = the second-to-last entry in price_history.
 * Falls back to the first entry when history has exactly one entry.
 *
 * Returns null when price_history is absent, empty, or the resolved value is invalid.
 *
 * @param {Object} options
 * @param {Object} options.product
 * @param {boolean} [options.sqm=false]   whether to return price per square meter
 * @returns {number|null}
 */
export function resolvePreviousPrice({product, sqm = false}) {
  const history = product.price_history
  if (!history || history.length === 0) return null

  const entry = history.slice(-2, -1)[0] ?? history[0]

  return resolvePrice({product, entry, sqm})
}

// ── Main entry point ───────────────────────────────────────────────────────────

/**
 * Computes all display-relevant price data for a product cell, row, or tile.
 *
 * Handles: price resolution, visibility gating, on-offer strikethrough,
 * and hidden placeholder text.
 *
 * Hidden placeholder text defaults:
 *   - sold product → hiddenSoldText (default: '-')
 *   - all others   → hiddenText     (default: '—')
 * Callers using i18n should pass translated strings via options
 * (e.g. hiddenText: t('ask_for_price')).
 *
 * @param {Object}  product
 * @param {Object}  [options]
 * @param {boolean} [options.sqm=false]             if true, formats price per m² instead of total (requires area from product.area_original)
 * @param {number}  [options.area=null]             area in m² for sqm price calculation
 * @param {boolean} [options.pricesVisible=true]
 * @param {boolean} [options.pricesSoldVisible=true]
 * @param {boolean} [options.pricesReservedVisible=true]
 * @param {string}  [options.hiddenText='—']        placeholder for non-sold hidden prices
 * @param {string}  [options.hiddenSoldText='-']    placeholder for sold hidden prices
 * @param {number}  [options.digits=2]              decimal places in formatted price
 * @param {string}  [options.currency='PLN']        ISO 4217 currency code
 * @param {string}  [options.locale='pl-PL']        IETF locale tag
 * @returns {{
 *   resolvedPrice: number|null,
 *   isHidden: boolean,
 *   isOnOffer: boolean,
 *   displayPrice: string,
 *   strikethroughPrice: string|null
 * }}
 */
export function formatProductPrice(product, {
  sqm = false,
  pricesVisible = true,
  pricesSoldVisible = true,
  pricesReservedVisible = true,
  hiddenText = '—',
  hiddenSoldText = '-',
  digits = 2,
  currency = 'PLN',
  locale = 'pl-PL',
} = {}) {
  const resolvedPrice = resolvePrice({product, sqm})
  const isOnOffer = product.price_offer?.on_offer === true
  const hidden = isPriceHidden(product, { pricesVisible, pricesSoldVisible, pricesReservedVisible })

  if (hidden) {
    const placeholder = product.status === SOLD ? hiddenSoldText : hiddenText
    return {
      resolvedPrice,
      isHidden: true,
      isOnOffer,
      displayPrice: placeholder,
      strikethroughPrice: null,
    }
  }

  if (resolvedPrice === null) {
    return {
      resolvedPrice: null,
      isHidden: false,
      isOnOffer,
      displayPrice: hiddenText,
      strikethroughPrice: null,
    }
  }

  const displayPrice = formatCurrency(resolvedPrice, { currency, locale, digits })

  let strikethroughPrice = null
  if (isOnOffer) {
    const prev = resolvePreviousPrice({product, sqm})
    if (prev !== null && prev !== resolvedPrice) {
      strikethroughPrice = formatCurrency(prev, { currency, locale, digits })
    }
  }

  return {
    resolvedPrice,
    isHidden: false,
    isOnOffer,
    displayPrice,
    strikethroughPrice,
  }
}

// ── EU Omnibus Directive ───────────────────────────────────────────────────────

/**
 * Returns the lowest price in the 31-day window before the most recent price change.
 *
 * Required by EU Omnibus Directive for promotional pricing display: when a seller
 * advertises a reduced price, they must show the lowest price from the 30 days
 * preceding the reduction.
 *
 * Algorithm:
 *   1. Take the timestamp of the last price_history entry (= the reduction date).
 *   2. Window = [reduction_date − 31 days, reduction_date).
 *   3. Collect all entries within the window, plus the last entry before the window.
 *   4. Return the minimum price across all collected entries.
 *
 * @param {Array}   priceHistory          product.price_history
 * @param {Object}  [options]
 * @param {boolean} [options.sqm=false]   if true, returns price per m² instead of total
 * @param {number|null} [options.area=null]  product.area_original (required when sqm=true)
 * @returns {number|null}
 */
export function getOmnibusLowestPrice(priceHistory, { sqm = false, area = null } = {}) {
  if (!priceHistory || priceHistory.length === 0) return null

  const lastEntry = priceHistory[priceHistory.length - 1]
  const reductionTs = new Date(lastEntry.date).getTime()
  if (isNaN(reductionTs)) return null

  const windowStart = reductionTs - 31 * 24 * 60 * 60 * 1000

  const inWindow = priceHistory.filter(entry => {
    const t = new Date(entry.date).getTime()
    return t >= windowStart && t < reductionTs
  })

  const beforeWindow = priceHistory.filter(entry =>
    new Date(entry.date).getTime() < windowStart
  )

  const lastBefore = beforeWindow.length > 0
    ? beforeWindow.reduce((latest, entry) =>
        new Date(entry.date) > new Date(latest.date) ? entry : latest,
        beforeWindow[0])
    : priceHistory[0] // fallback: first ever entry

  const relevant = lastBefore ? [lastBefore, ...inWindow] : inWindow

  if (relevant.length === 0) {
    const fallback = priceHistory[0]
    if (sqm) {
      return fallback.price_m2_brutto ?? (area ? fallback.price_brutto / area : null)
    }
    return parseFloat(fallback.price_brutto) || null
  }

  if (sqm) {
    const values = relevant.map(e => e.price_m2_brutto ?? (area ? e.price_brutto / area : null))
    const valid = values.filter(v => v != null && !isNaN(v))
    return valid.length > 0 ? Math.min(...valid) : null
  }

  const values = relevant.map(e => parseFloat(e.price_brutto)).filter(v => !isNaN(v) && v > 0)
  return values.length > 0 ? Math.min(...values) : null
}

// ── Panel settings helpers ─────────────────────────────────────────────────────

/**
 * Extracts the three price-visibility flags from `panelSettings.prices_visibility`.
 *
 * Cross-domain utility — used by all catalog views (List, Tiles, Cards) and any
 * future domain that renders product prices.
 *
 * Defaults to all-visible when the key is absent (safe for public pages and legacy
 * panel configs that predate the prices_visibility block).
 *
 * @param {object|null|undefined} panelSettings
 * @returns {{ pricesVisible: boolean, pricesSoldVisible: boolean, pricesReservedVisible: boolean }}
 */
export function parsePricesVisibility(panelSettings) {
  const v = panelSettings?.prices_visibility ?? {}
  return {
    pricesVisible:         v.prices_visible         !== false,
    pricesSoldVisible:     v.prices_sold_visible     !== false,
    pricesReservedVisible: v.prices_reserved_visible !== false,
  }
}
